/**
 * B2B Discount Progress Widget
 * Displays tiered discount progress for wholesale customers
 * Updates in real-time as cart value changes
 */

(function() {
  'use strict';

  // Configuration constants
  const TIER_1_THRESHOLD = 50000;  // $500 in cents
  const TIER_1_DISCOUNT = 15;      // 15%
  const TIER_2_THRESHOLD = 200000; // $2000 in cents
  const TIER_2_DISCOUNT = 20;      // 20%

  // Required customer tags
  const REQUIRED_TAGS = ['upnova-wholesale', 'Wholesale'];
  
  // Allowed domains (users shop on main domain, portal is separate)
  const ALLOWED_DOMAINS = ['countrylifefoods.com', 'business.countrylifefoods.com'];

  // Store observer references for cleanup
  let observerInstances = {
    drawer: null,
    cart: null,
    total: null
  };

  // Track previous tier to detect tier changes
  let previousTier = null;

  /**
   * Trigger confetti celebration effect
   * @param {HTMLElement} widget - The widget element to position confetti around
   * @param {boolean} isMax - Whether this is the max tier (more confetti)
   */
  function triggerConfetti(widget, isMax) {
    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const colors = isMax 
      ? ['#fbbf24', '#f59e0b', '#d97706', '#22c55e', '#16a34a'] // Gold/green for max
      : ['#22c55e', '#16a34a', '#15803d', '#4ade80']; // Green for tier 1
    
    const confettiCount = isMax ? 40 : 25;
    const container = widget.querySelector('.b2b-discount-widget__container') || widget;
    
    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'b2b-confetti';
      confetti.style.cssText = `
        position: absolute;
        width: ${Math.random() * 8 + 4}px;
        height: ${Math.random() * 8 + 4}px;
        background: ${colors[Math.floor(Math.random() * colors.length)]};
        left: ${Math.random() * 100}%;
        top: 50%;
        opacity: 1;
        border-radius: ${Math.random() > 0.5 ? '50%' : '0'};
        pointer-events: none;
        z-index: 10;
      `;
      
      container.style.position = 'relative';
      container.style.overflow = 'visible';
      container.appendChild(confetti);
      
      // Animate each piece
      const angle = (Math.random() * 360) * (Math.PI / 180);
      const velocity = Math.random() * 80 + 40;
      const dx = Math.cos(angle) * velocity;
      const dy = Math.sin(angle) * velocity - 60; // Bias upward
      
      confetti.animate([
        { transform: 'translate(0, 0) rotate(0deg)', opacity: 1 },
        { transform: `translate(${dx}px, ${dy}px) rotate(${Math.random() * 360}deg)`, opacity: 0 }
      ], {
        duration: 1000 + Math.random() * 500,
        easing: 'cubic-bezier(0, 0.5, 0.5, 1)'
      }).onfinish = () => confetti.remove();
    }
  }

  /**
   * Check if customer has required tags
   */
  function hasRequiredTags() {
    // Wait for customerTags to be available (might be set asynchronously)
    // Check if it exists and is an array
    if (!window.customerTags) {
      // If not set yet, return false but don't initialize to empty array
      // This allows the check to retry later
      return false;
    }
    
    if (!Array.isArray(window.customerTags)) {
      return false;
    }
    
    // If array is empty, customer might not be logged in or has no tags
    if (window.customerTags.length === 0) {
      return false;
    }
    
    const customerTagsLower = window.customerTags.map(tag => String(tag).toLowerCase());
    return REQUIRED_TAGS.some(tag => customerTagsLower.includes(tag.toLowerCase()));
  }

  /**
   * Check if on allowed domain
   */
  function isAllowedDomain() {
    if (!window.location || !window.location.hostname) {
      return false;
    }
    
    const hostname = window.location.hostname.toLowerCase();
    // Remove www. prefix if present for comparison
    const hostnameWithoutWww = hostname.replace(/^www\./, '');
    
    // Allow main domain (with or without www) and business portal domain
    return ALLOWED_DOMAINS.some(domain => {
      const domainLower = domain.toLowerCase();
      return hostname === domainLower || 
             hostnameWithoutWww === domainLower || 
             hostname.endsWith('.' + domainLower);
    });
  }

  /**
   * Check if widget should be displayed
   */
  function shouldDisplayWidget() {
    return hasRequiredTags() && isAllowedDomain();
  }

  /**
   * Get money format from theme
   */
  function getMoneyFormat() {
    try {
      const wethemeGlobal = document.getElementById('wetheme-global');
      if (wethemeGlobal) {
        const data = JSON.parse(wethemeGlobal.textContent);
        if (data.moneyFormat) {
          return data.moneyFormat;
        }
      }
    } catch (e) {
      // Fallback if parsing fails
    }
    return '${{amount}}'; // Default USD format
  }

  /**
   * Format currency from cents using Shopify money format
   */
  function formatCurrency(cents) {
    // Validate input
    if (typeof cents !== 'number' || isNaN(cents) || cents < 0) {
      cents = 0;
    }

    const moneyFormat = getMoneyFormat();
    const amount = (cents / 100).toFixed(2);
    
    // Handle Shopify money format placeholders
    if (moneyFormat.includes('{{amount}}')) {
      // Format with thousands separator
      const formatted = parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      return moneyFormat.replace('{{amount}}', formatted);
    } else if (moneyFormat.includes('{{amount_no_decimals}}')) {
      const formatted = Math.round(cents / 100).toLocaleString('en-US');
      return moneyFormat.replace('{{amount_no_decimals}}', formatted);
    } else {
      // Fallback to standard currency formatting
      try {
        // Try to get currency from cart
        const currency = window.Shopify?.currency?.active || 'USD';
        return (cents / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      } catch (e) {
        // Ultimate fallback
        return '$' + (cents / 100).toFixed(2);
      }
    }
  }

  /**
   * Calculate discount progress state
   */
  function calculateProgress(cartTotal) {
    if (cartTotal < TIER_1_THRESHOLD) {
      // Before first tier
      return {
        currentTier: 0,
        nextThreshold: TIER_1_THRESHOLD,
        nextDiscount: TIER_1_DISCOUNT,
        amountRemaining: TIER_1_THRESHOLD - cartTotal,
        progressPercent: (cartTotal / TIER_1_THRESHOLD) * 100,
        achieved: false
      };
    } else if (cartTotal < TIER_2_THRESHOLD) {
      // First tier achieved, working toward second
      return {
        currentTier: 1,
        nextThreshold: TIER_2_THRESHOLD,
        nextDiscount: TIER_2_DISCOUNT,
        amountRemaining: TIER_2_THRESHOLD - cartTotal,
        progressPercent: ((cartTotal - TIER_1_THRESHOLD) / (TIER_2_THRESHOLD - TIER_1_THRESHOLD)) * 100,
        achieved: true,
        achievedTier: 1,
        achievedDiscount: TIER_1_DISCOUNT
      };
    } else {
      // Maximum tier achieved
      return {
        currentTier: 2,
        nextThreshold: null,
        nextDiscount: null,
        amountRemaining: 0,
        progressPercent: 100,
        achieved: true,
        achievedTier: 2,
        achievedDiscount: TIER_2_DISCOUNT
      };
    }
  }

  /**
   * Update widget UI based on cart state
   */
  function updateWidget(cart) {
    const widget = document.getElementById('b2b-discount-progress-widget');
    
    if (!widget) {
      return;
    }

    // Check if should display
    if (!shouldDisplayWidget()) {
      widget.style.display = 'none';
      return;
    }

    // Hide if cart is empty
    if (!cart || cart.item_count === 0) {
      widget.style.display = 'none';
      return;
    }

    // Get cart total - prefer original_total_price (before discounts) for accurate progress calculation
    // If original_total_price is not available, use total_price
    // This ensures we calculate progress based on subtotal before any discounts are applied
    let cartTotal = 0;
    if (cart.original_total_price !== undefined && cart.original_total_price !== null) {
      cartTotal = parseInt(cart.original_total_price, 10) || 0;
    } else if (cart.total_price !== undefined && cart.total_price !== null) {
      cartTotal = parseInt(cart.total_price, 10) || 0;
    } else if (cart.items_subtotal_price !== undefined) {
      cartTotal = parseInt(cart.items_subtotal_price, 10) || 0;
    }
    
    const progress = calculateProgress(cartTotal);

    // Show widget
    widget.style.setProperty('display', 'block', 'important');

    // Get DOM elements
    const badge = document.getElementById('b2b-discount-badge');
    const title = document.getElementById('b2b-discount-title');
    const message = document.getElementById('b2b-discount-message');
    const cartValue = document.getElementById('b2b-discount-cart-value');
    const progressFill = document.getElementById('b2b-discount-progress-fill');
    const progressPercent = document.getElementById('b2b-discount-progress-percent');
    const progressBar = document.getElementById('b2b-discount-progress-bar');

    if (!badge || !title || !message || !cartValue || !progressFill || !progressPercent || !progressBar) {
      return;
    }

    // Update cart value
    cartValue.textContent = formatCurrency(cartTotal);

    // Update progress bar
    const percent = Math.min(100, Math.max(0, progress.progressPercent));
    progressFill.style.width = percent + '%';
    progressPercent.textContent = Math.round(percent) + '%';
    progressBar.setAttribute('aria-valuenow', Math.round(percent));

    // Detect tier changes
    const tierChanged = previousTier !== null && previousTier !== progress.currentTier;
    const tierIncreased = tierChanged && progress.currentTier > previousTier;
    const tierDecreased = tierChanged && progress.currentTier < previousTier;

    // Remove any existing transition classes before applying new state
    widget.classList.remove('b2b-discount-widget--tier-achieved', 'b2b-discount-widget--max-achieved', 'b2b-discount-widget--tier-dropped');

    // If tier decreased, add a smooth transition class
    if (tierDecreased) {
      widget.classList.add('b2b-discount-widget--tier-dropped');
      setTimeout(() => {
        widget.classList.remove('b2b-discount-widget--tier-dropped');
      }, 600);
    }

    // Update based on tier state
    if (progress.currentTier === 0) {
      // Before first tier
      badge.className = 'b2b-discount-widget__badge';
      badge.innerHTML = '';
      badge.setAttribute('aria-hidden', 'true');
      title.textContent = 'Unlock 15% Discount';
      message.textContent = `Add ${formatCurrency(progress.amountRemaining)} more to save 15% on your order`;
      progressFill.className = 'b2b-discount-widget__progress-fill';
    } else if (progress.currentTier === 1) {
      // First tier achieved
      badge.className = 'b2b-discount-widget__badge b2b-discount-widget__badge--achieved';
      badge.innerHTML = '<span class="b2b-discount-widget__checkmark">✓</span> 15% Unlocked';
      badge.setAttribute('aria-hidden', 'false');
      title.textContent = 'Great! Now unlock 20% discount';
      message.textContent = `Add ${formatCurrency(progress.amountRemaining)} more to save 20% on your order`;
      progressFill.className = 'b2b-discount-widget__progress-fill b2b-discount-widget__progress-fill--tier2';
      
      // Only trigger celebration when first entering this tier (going up)
      if (tierIncreased) {
        widget.classList.add('b2b-discount-widget--tier-achieved');
        triggerConfetti(widget);
        setTimeout(() => {
          widget.classList.remove('b2b-discount-widget--tier-achieved');
        }, 2000);
      }
    } else {
      // Maximum tier achieved
      badge.className = 'b2b-discount-widget__badge b2b-discount-widget__badge--max';
      badge.innerHTML = '<span class="b2b-discount-widget__checkmark">✓</span> 20% Maximum Discount!';
      badge.setAttribute('aria-hidden', 'false');
      title.textContent = 'Congratulations! 🎉';
      message.textContent = "You've unlocked the maximum 20% discount";
      progressFill.className = 'b2b-discount-widget__progress-fill b2b-discount-widget__progress-fill--max';
      progressPercent.textContent = '100%';
      
      // Only trigger celebration when first entering this tier (going up)
      if (tierIncreased) {
        widget.classList.add('b2b-discount-widget--max-achieved');
        triggerConfetti(widget, true); // More confetti for max tier
        setTimeout(() => {
          widget.classList.remove('b2b-discount-widget--max-achieved');
        }, 2000);
      }
    }

    // Update previous tier for next comparison
    previousTier = progress.currentTier;
  }

  /**
   * Get cart data from Vue component or fetch from API
   */
  function getCartData() {
    // Try to get from Vue component first (if Vue instance is accessible)
    try {
      const cartContainer = document.getElementById('CartContainer');
      if (cartContainer && cartContainer.__vue__) {
        const vueInstance = cartContainer.__vue__;
        if (vueInstance.cart) {
          return Promise.resolve(vueInstance.cart);
        }
      }
    } catch (e) {
      // Vue instance not accessible, continue to other methods
    }

    // Try wetheme API
    if (window.wetheme && window.wetheme.getCart) {
      try {
        const cart = window.wetheme.getCart();
        if (cart) return Promise.resolve(cart);
      } catch (e) {
        // Continue to next method
      }
    }

    // Try to read from DOM (Vue renders cart data)
    // Note: .cart__total has v-text="cart.total_price" which outputs raw cents (e.g., 506 for $5.06)
    // .ajaxcart__subtotal p has v-money which outputs formatted dollars (e.g., "$5.06")
    const cartTotalElement = document.querySelector('.cart__total, .hide.cart__total');
    if (cartTotalElement && cartTotalElement.textContent) {
      const text = cartTotalElement.textContent.trim().replace(/[^0-9.]/g, '');
      const totalPrice = parseFloat(text);
      // This is already in cents from Vue's v-text binding, don't multiply by 100
      if (!isNaN(totalPrice) && totalPrice > 0) {
        return Promise.resolve({ 
          original_total_price: totalPrice, 
          total_price: totalPrice,
          item_count: 1 // Assume at least one item if total exists
        });
      }
    }
    
    // Fallback to formatted money element (this IS in dollars, needs conversion)
    const formattedCartElement = document.querySelector('.ajaxcart__subtotal p');
    if (formattedCartElement && formattedCartElement.textContent) {
      const text = formattedCartElement.textContent.trim().replace(/[^0-9.]/g, '');
      const totalPrice = parseFloat(text) * 100; // Convert dollars to cents
      if (!isNaN(totalPrice) && totalPrice > 0) {
        return Promise.resolve({ 
          original_total_price: totalPrice, 
          total_price: totalPrice,
          item_count: 1
        });
      }
    }

    // Fallback to API
    return fetch('/cart.js')
      .then(response => {
        if (!response.ok) {
          throw new Error('Cart API response not ok');
        }
        return response.json();
      })
      .catch(error => {
        console.error('B2B Discount Widget: Error fetching cart:', error);
        return { original_total_price: 0, total_price: 0, item_count: 0 };
      });
  }

  /**
   * Initialize widget
   */
  function initWidget() {
    if (window.__clnfB2bWidgetInitialized) return;

    // Wait a bit for customerTags to be set if it's not ready yet
    if (!window.customerTags && document.readyState !== 'complete') {
      setTimeout(function() {
        initWidget();
      }, 100);
      return;
    }

    // Check if widget should be displayed
    if (!shouldDisplayWidget()) {
      return;
    }

    window.__clnfB2bWidgetInitialized = true;

    // Initial update
    getCartData().then(cart => {
      updateWidget(cart);
    });

    // Use a single debounced function to avoid multiple simultaneous calls
    // across the drawer/cart/total observers below.
    let updateTimeout = null;
    const debouncedUpdate = function() {
      clearTimeout(updateTimeout);
      updateTimeout = setTimeout(function() {
        getCartData().then(updateWidget);
      }, 300); // Longer debounce to prevent flickering
    };

    // Watch for cart drawer open/close (attribute changes on CartContainer).
    // Debounced like the observers below: Vue can toggle style/class more than
    // once per open/close transition, and each raw mutation record forces a
    // synchronous offsetParent layout read before triggering a full cart
    // fetch + widget re-render.
    const drawerContainer = document.getElementById('CartContainer');
    if (drawerContainer) {
      const drawerObserver = new MutationObserver(function() {
        if (drawerContainer.offsetParent !== null) {
          debouncedUpdate();
        }
      });
      drawerObserver.observe(drawerContainer, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: false
      });
      observerInstances.drawer = drawerObserver;
    }

    // Listen for cart update events
    const cartUpdatedHandler = function(event) {
      let cart = null;
      
      // Try multiple ways to extract cart data from event
      if (event.detail) {
        // Direct cart object
        cart = event.detail.cart;
        
        // Try sections API response
        if (!cart && event.detail.sections) {
          // Sections API returns HTML strings, need to parse or fetch fresh
          if (event.detail.sections.cart) {
            // If it's a string (HTML), we need to fetch fresh cart data
            // If it's an object, use it directly
            if (typeof event.detail.sections.cart === 'object') {
              cart = event.detail.sections.cart;
            }
          }
        }
      }
      
      if (cart && typeof cart === 'object') {
        updateWidget(cart);
      } else {
        // Fallback to fetching fresh cart data
        getCartData().then(updateWidget);
      }
    };

    const cartRefreshHandler = function(event) {
      getCartData().then(updateWidget);
    };

    document.addEventListener('cart:updated', cartUpdatedHandler);
    document.addEventListener('cart:refresh', cartRefreshHandler);

    // Listen for Vue cart updates (if available)
    if (window.wetheme && window.wetheme.onCartUpdate) {
      window.wetheme.onCartUpdate(function(cart) {
        updateWidget(cart);
      });
    }

    // Also listen for mutations in the cart drawer (Vue component updates).
    // Shares the debouncedUpdate() declared above with drawerObserver.
    const cartObserver = new MutationObserver(function(mutations) {
      debouncedUpdate();
    });

    const observedCartContainer = document.getElementById('CartContainer');
    if (observedCartContainer) {
      cartObserver.observe(observedCartContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'data-line']
      });
      observerInstances.cart = cartObserver;
    }

    // Also watch for changes to cart total display
    const cartTotalObserver = new MutationObserver(function() {
      debouncedUpdate();
    });

    const cartTotalElement = document.querySelector('.cart__total, .ajaxcart__subtotal p');
    if (cartTotalElement) {
      cartTotalObserver.observe(cartTotalElement, {
        childList: true,
        characterData: true,
        subtree: true
      });
      observerInstances.total = cartTotalObserver;
    }

    // Store cleanup function
    window.b2bWidgetCleanup = function() {
      if (observerInstances.drawer) {
        observerInstances.drawer.disconnect();
        observerInstances.drawer = null;
      }
      if (observerInstances.cart) {
        observerInstances.cart.disconnect();
        observerInstances.cart = null;
      }
      if (observerInstances.total) {
        observerInstances.total.disconnect();
        observerInstances.total = null;
      }
      if (updateTimeout) {
        clearTimeout(updateTimeout);
        updateTimeout = null;
      }
      document.removeEventListener('cart:updated', cartUpdatedHandler);
      document.removeEventListener('cart:refresh', cartRefreshHandler);
    };
  }

  // Initialize when DOM is ready
  function startInit() {
    // Wait for customerTags to be available (it's set in theme.liquid)
    // Check if it exists, and if not, wait a bit
    function waitForCustomerTags(callback, maxAttempts) {
      maxAttempts = maxAttempts || 10;
      let attempts = 0;
      
      function check() {
        attempts++;
        // customerTags might be undefined (not logged in) or an array
        // If it's explicitly set (even to empty array), we're good
        if (window.hasOwnProperty('customerTags') || attempts >= maxAttempts) {
          callback();
        } else {
          setTimeout(check, 100);
        }
      }
      check();
    }
    
    // Use MutationObserver to detect when cart drawer is ready (more efficient than polling)
    const initObserver = new MutationObserver(function(mutations, obs) {
      const cartContainer = document.getElementById('CartContainer');
      const widget = document.getElementById('b2b-discount-progress-widget');
      
      // If either the cart container or widget exists, we can initialize
      if (cartContainer || widget) {
        obs.disconnect();
        waitForCustomerTags(function() {
          initWidget();
        });
      }
    });

    // Start observing the document body for the cart container or widget
    if (document.body) {
      initObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    // Fallback: Also check immediately and use a timeout
    const immediateCheck = function() {
      const cartContainer = document.getElementById('CartContainer');
      const widget = document.getElementById('b2b-discount-progress-widget');
      if (cartContainer || widget) {
        initObserver.disconnect();
        waitForCustomerTags(function() {
          initWidget();
        });
        return true;
      }
      return false;
    };

    // Check immediately
    if (!immediateCheck()) {
      // If not ready, check periodically with increasing intervals (more efficient)
      let checkCount = 0;
      const maxChecks = 20; // 20 checks over ~5 seconds
      const checkInterval = setInterval(function() {
        checkCount++;
        if (immediateCheck() || checkCount >= maxChecks) {
          clearInterval(checkInterval);
          initObserver.disconnect();
          if (checkCount >= maxChecks) {
            // Final attempt even if elements aren't found
            waitForCustomerTags(function() {
              initWidget();
            });
          }
        }
      }, 250); // Check every 250ms instead of 100ms
    } else {
      // If found immediately, still wait for customerTags
      waitForCustomerTags(function() {
        initWidget();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startInit);
  } else {
    startInit();
  }

  // Cleanup on page unload
  window.addEventListener('beforeunload', function() {
    if (window.b2bWidgetCleanup) {
      window.b2bWidgetCleanup();
    }
  });
})();
